import * as React from 'react';
import { Alert, Box, Button, Card, CardContent, CircularProgress, FormControl, InputLabel, MenuItem, Pagination, Select, Snackbar, Stack, TextField, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { generateVirtualLeague, getVirtualCatalogLeagues, getVirtualCatalogSports } from '../Api';
import VirtualCatalogAddMenu from '../components/virtual-sports/VirtualCatalogAddMenu';
import VirtualImmediateGenerationDialog from '../components/virtual-sports/VirtualImmediateGenerationDialog';
import VirtualLeagueCreationDialog from '../components/virtual-sports/VirtualLeagueCreationDialog';
import VirtualLeaguesTable from '../components/virtual-sports/VirtualLeaguesTable';
import VirtualLeagueDetailsDialog from '../components/virtual-sports/VirtualLeagueDetailsDialog';
import VirtualLeagueImportDialog from '../components/virtual-sports/VirtualLeagueImportDialog';
import VirtualActivityLogBell from '../components/virtual-sports/VirtualActivityLogBell';
import { VirtualCatalogLeagueDto, VirtualCatalogSportDto, VirtualLeagueSort } from '../models/dtos/VirtualSportsCatalogDto';

const PAGE_SIZE = 25;

export default function VirtualSportsLeaguesPage() {
    const navigate = useNavigate();
    const [page, setPage] = React.useState(0);
    const [searchTerm, setSearchTerm] = React.useState('');
    const [sort, setSort] = React.useState<VirtualLeagueSort>('MOST_RECENT');
    const [loading, setLoading] = React.useState(true);
    const [leagues, setLeagues] = React.useState<VirtualCatalogLeagueDto[]>([]);
    const [totalItems, setTotalItems] = React.useState(0);
    const [sports, setSports] = React.useState<VirtualCatalogSportDto[]>([]);
    const [manualOpen, setManualOpen] = React.useState(false);
    const [immediateOpen, setImmediateOpen] = React.useState(false);
    const [importOpen, setImportOpen] = React.useState(false);
    const [reloadKey, setReloadKey] = React.useState(0);
    const [error, setError] = React.useState('');
    const [notice, setNotice] = React.useState('');
    const [selectedLeagueSyncId, setSelectedLeagueSyncId] = React.useState<string | null>(null);

    React.useEffect(() => {
        getVirtualCatalogSports().then(setSports).catch(reason =>
            setError(reason instanceof Error ? reason.message : 'Could not load virtual sports'));
    }, []);

    React.useEffect(() => {
        let cancelled = false;
        setLoading(true);
        const timeout = setTimeout(() => {
            getVirtualCatalogLeagues(page * PAGE_SIZE, PAGE_SIZE, sort, searchTerm)
                .then(result => {
                    if (cancelled) return;
                    setLeagues(result.items);
                    setTotalItems(result.totalItems);
                })
                .catch(reason => { if (!cancelled) setError(reason instanceof Error ? reason.message : 'Could not load virtual leagues'); })
                .finally(() => { if (!cancelled) setLoading(false); });
        }, searchTerm ? 350 : 0);
        return () => {
            cancelled = true;
            clearTimeout(timeout);
        };
    }, [page, searchTerm, sort, reloadKey]);

    const catalogCreated = (name: string) => {
        setNotice(`${name} was created. It will appear here after catalog synchronization.`);
        setPage(0);
        setReloadKey(value => value + 1);
    };

    return (
        <Stack spacing={3} sx={{ mx: { xs: 2, md: 5 }, my: 5 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="h3">Virtual leagues</Typography>
                    <Typography color="text.secondary">Leagues synchronized from the virtual sports provider.</Typography>
                </Box>
                <VirtualCatalogAddMenu
                    entityLabel="league"
                    onAdd={() => setManualOpen(true)}
                    onGenerateImmediately={() => setImmediateOpen(true)}
                    onImportFromFile={() => setImportOpen(true)}
                />
                <VirtualActivityLogBell />
                <Button variant="outlined" onClick={() => navigate('/virtual-sports')}>Back to overview</Button>
            </Stack>

            {error ? <Alert severity="error">{error}</Alert> : null}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                    label="Search by league name"
                    type="search"
                    value={searchTerm}
                    onChange={event => { setSearchTerm(event.target.value); setPage(0); }}
                    sx={{ flex: 1 }}
                />
                <FormControl sx={{ minWidth: 220 }}>
                    <InputLabel id="virtual-league-sort-label">Sort</InputLabel>
                    <Select
                        labelId="virtual-league-sort-label"
                        label="Sort"
                        value={sort}
                        onChange={event => { setSort(event.target.value as VirtualLeagueSort); setPage(0); }}
                    >
                        <MenuItem value="MOST_RECENT">Most recent</MenuItem>
                        <MenuItem value="MORE_MATCHES">More matches</MenuItem>
                        <MenuItem value="ALPHABETICAL">Alphabetical</MenuItem>
                    </Select>
                </FormControl>
            </Stack>

            <Card variant="outlined">
                <CardContent>
                    {loading ? <Box textAlign="center"><CircularProgress /></Box> : (
                        <VirtualLeaguesTable
                            leagues={leagues}
                            onViewMatches={league => navigate(`/virtual-sports/matches?leagueId=${league.sync_id ?? ''}`)}
                            onLeagueClick={league => setSelectedLeagueSyncId(league.sync_id ?? null)}
                        />
                    )}
                </CardContent>
            </Card>

            {totalItems > PAGE_SIZE ? (
                <Pagination
                    count={Math.ceil(totalItems / PAGE_SIZE)}
                    page={page + 1}
                    onChange={(_, value) => setPage(value - 1)}
                    sx={{ alignSelf: 'center' }}
                />
            ) : null}

            <VirtualLeagueCreationDialog
                open={manualOpen}
                sports={sports}
                onClose={() => setManualOpen(false)}
                onCreated={league => catalogCreated(league.name)}
            />
            <VirtualImmediateGenerationDialog
                open={immediateOpen}
                entityLabel="league"
                sports={sports}
                onClose={() => setImmediateOpen(false)}
                onGenerate={async sportId => {
                    const league = await generateVirtualLeague(sportId);
                    catalogCreated(league.name);
                }}
            />
            <VirtualLeagueImportDialog
                open={importOpen}
                onClose={() => setImportOpen(false)}
                onImported={result => {
                    setNotice(`${result.importedCount} ${result.importedCount === 1 ? 'league was' : 'leagues were'} imported. They will appear after catalog synchronization.`);
                    setPage(0);
                    setReloadKey(value => value + 1);
                }}
            />
            <VirtualLeagueDetailsDialog
                leagueSyncId={selectedLeagueSyncId}
                onClose={() => setSelectedLeagueSyncId(null)}
                onUpdated={updated => {
                    setLeagues(current => current.map(league => league.sync_id === updated.id
                        ? { ...league, photo_url: updated.imageUrl }
                        : league));
                    setNotice(`${updated.name} was updated.`);
                }}
            />
            <Snackbar open={Boolean(notice)} autoHideDuration={6000} onClose={() => setNotice('')} message={notice} />
        </Stack>
    );
}
